import { EyeIcon } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { PercentageOrEmpty } from '../../../components/typography/percentage-or-empty.tsx';
import { Badge } from '../../../components/ui/badge.tsx';
import { Card } from '../../../components/ui/card.tsx';
import { CollapsibleContainer } from '../../../components/ui/collapsible-container.tsx';
import { Text } from '../../../components/ui/text.tsx';
import { toCurrencyString } from '../../../lib/currency-helpers.ts';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { cn } from '../../../lib/utils.ts';
import type { ProductVariantPricingSummary } from '../../../server/queries/product-variant-query-pricing-summary.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';

type Props = {
    productVariantId: string;
    customerId?: string;
    data: ProductVariantPricingSummary;
};

export const InventoryDetailsPricingHistory = ({ productVariantId, customerId, data }: Props): ReactNode => {
    const { t, locale } = useLocale('product-details');
    const sendEvent = useAnalytics();
    const [revealed, setRevealed] = useState(false);

    if (
        data.minUnitPrice === undefined ||
        data.medianUnitPrice === undefined ||
        data.maxUnitPrice === undefined ||
        data.medianBuyPrice === undefined
    ) {
        return (
            <CollapsibleContainer title={t('pricing_history__title')} defaultOpen>
                <Card>
                    <Text size="sm" color="neutral-500">
                        {t('pricing_history__no_results')}
                    </Text>
                </Card>
            </CollapsibleContainer>
        );
    }

    const handleReveal = (): void => {
        setRevealed(true);

        sendEvent('pricing_discovery_show_blurred_history_click', {
            product_variant_id: productVariantId,
            customer_id: customerId,
        });
    };

    const renderMarginChip = (salePrice: number): ReactNode => {
        if (data.medianBuyPrice === undefined) {
            return;
        }

        const marginRatio = (salePrice - data.medianBuyPrice) / salePrice;
        const variant = marginRatio >= 0 ? 'positive' : 'warning';

        return (
            <Badge variant={variant} className="rounded border-0 gap-0.5 px-1 text-xs font-normal">
                <span>{t('pricing_history__margin_chip_prefix')}</span>
                <span>
                    <PercentageOrEmpty decimals={1} showPlusSignIfPositive>
                        {marginRatio}
                    </PercentageOrEmpty>
                </span>
            </Badge>
        );
    };

    return (
        <CollapsibleContainer title={t('pricing_history__title')} defaultOpen>
            <Card>
                <div className="relative w-fit">
                    <div
                        className={cn(
                            'flex items-start gap-x-5',
                            !revealed && 'pointer-events-none select-none blur-sm',
                        )}
                        aria-hidden={!revealed}
                    >
                        {/* Current Price */}
                        <div className="flex flex-col items-start gap-1">
                            <Text size="xs" color="neutral-500">
                                {t('pricing_history__label_procurement')}
                            </Text>
                            <span className="text-sm tabular-nums font-normal text-neutral-500">
                                {toCurrencyString(data.medianBuyPrice, locale, 2)}
                            </span>
                        </div>
                        {/* Seperator */}
                        <div className="w-px self-stretch bg-neutral-100" />
                        {/* Min Price */}
                        <div className="flex flex-col items-start gap-1">
                            <Text size="xs" color="neutral-500">
                                {t('pricing_history__label_min')}
                            </Text>
                            <span className="text-sm tabular-nums text-neutral-900">
                                {toCurrencyString(data.minUnitPrice, locale, 2)}
                            </span>
                            {renderMarginChip(data.minUnitPrice)}
                        </div>
                        {/* Median Price */}
                        <div className="flex flex-col items-start gap-1">
                            <Text size="xs" color="neutral-500">
                                {t('pricing_history__label_median')}
                            </Text>
                            <span className="text-sm tabular-nums text-neutral-900 font-semibold">
                                {toCurrencyString(data.medianUnitPrice, locale, 2)}
                            </span>
                            {renderMarginChip(data.medianUnitPrice)}
                        </div>
                        {/* Max Price */}
                        <div className="flex flex-col items-start gap-1">
                            <Text size="xs" color="neutral-500">
                                {t('pricing_history__label_max')}
                            </Text>
                            <span className="text-sm tabular-nums text-neutral-900">
                                {toCurrencyString(data.maxUnitPrice, locale, 2)}
                            </span>
                            {renderMarginChip(data.maxUnitPrice)}
                        </div>
                    </div>
                    {!revealed && (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <button
                                type="button"
                                onClick={handleReveal}
                                className="inline-flex h-8 w-fit items-center gap-1.5 rounded-md bg-neutral-50 px-3 text-sm font-normal text-neutral-700 transition-colors hover:bg-neutral-100 hover:text-neutral-950"
                            >
                                <EyeIcon className="h-4 w-4" />
                                {t('pricing_history__reveal_info')}
                            </button>
                        </div>
                    )}
                </div>
            </Card>
        </CollapsibleContainer>
    );
};
